import { Helmet } from '@dr.pogodin/react-helmet';
import PropTypes from 'prop-types';
import { DEFAULT_OG_IMAGE, absoluteUrl } from '../constants/seo.js';
import { loginMainImage, loginMainImageMobile } from '../../api/loginImages.js';
import { LOGIN_HERO_SIZES, LOGIN_HERO_SRCSET } from '../../Auth/AuthMainImage.jsx';
import nunitoSansSemiBold from '@fontsource/nunito-sans/files/nunito-sans-latin-600-normal.woff2?url';

// Single source of truth for per-page SEO tags: title, description, canonical,
// Open Graph / Twitter cards, robots directives and JSON-LD structured data.
// Relies on Helmet deduping so page-level tags override the global
// defaults declared in AppProviders.
// Helmet children must be native head tags only — not React components.
export default function Seo({
    title,
    description,
    canonical,
    image,
    type = 'website',
    noindex = false,
    jsonLd = null,
    authHeroPreload = false,
    children = null,
}) {
    const canonicalUrl = absoluteUrl(canonical);
    const ogImage = absoluteUrl(image) || DEFAULT_OG_IMAGE;
    const jsonLdItems = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

    return (
        <Helmet>
            {title ? <title>{title}</title> : null}
            {description ? <meta name="description" content={description} /> : null}
            {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
            {noindex ? <meta name="robots" content="noindex, follow" /> : null}

            <meta property="og:type" content={type} />
            {title ? <meta property="og:title" content={title} /> : null}
            {description ? <meta property="og:description" content={description} /> : null}
            {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
            <meta property="og:image" content={ogImage} />

            <meta name="twitter:card" content="summary_large_image" />
            {title ? <meta name="twitter:title" content={title} /> : null}
            {description ? <meta name="twitter:description" content={description} /> : null}
            <meta name="twitter:image" content={ogImage} />

            {jsonLdItems.map((item, index) => (
                <script key={index} type="application/ld+json">
                    {JSON.stringify(item)}
                </script>
            ))}

            {authHeroPreload ? (
                <>
                    <link
                        rel="preload"
                        as="font"
                        type="font/woff2"
                        href={nunitoSansSemiBold}
                        crossOrigin="anonymous"
                    />
                    <link
                        rel="preload"
                        as="image"
                        href={loginMainImageMobile}
                        imagesrcset={LOGIN_HERO_SRCSET}
                        imagesizes={LOGIN_HERO_SIZES}
                        media="(max-width: 768px)"
                        fetchpriority="high"
                    />
                    <link
                        rel="preload"
                        as="image"
                        href={loginMainImage}
                        media="(min-width: 769px)"
                        fetchpriority="high"
                    />
                </>
            ) : null}

            {children}
        </Helmet>
    );
}

Seo.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    canonical: PropTypes.string,
    image: PropTypes.string,
    type: PropTypes.string,
    noindex: PropTypes.bool,
    authHeroPreload: PropTypes.bool,
    jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    children: PropTypes.node,
};
